







































import { Component, Emit, Prop, Ref, Vue, Watch } from 'vue-property-decorator';

import MapboxService from '@/services/mapbox.service';
import { debounce } from '@/services/utils.service';
import { GeocodeFeature } from '@mapbox/mapbox-sdk/services/geocoding';
import { MapStore } from '@/store/map';
import { getModule } from 'vuex-module-decorators';
import { Route } from 'vue-router';

@Component
export default class Autocomplete extends Vue {
  @Prop({ required: true }) readonly setBottomSheet!: (type: string) => void;

  @Ref() readonly input!: HTMLInputElement;

  @Emit()
  newLocation(location: GeocodeFeature) {
    return location;
  }

  @Emit()
  noResults() {
    return;
  }

  search = '';
  autocomplete: GeocodeFeature[] = [];
  autocompleteIndex: null | number = null;
  dropLocation = false;
  process: string = process.env.VUE_APP_PROJECT;

  private _mapStore!: MapStore;
  private _debounceInput!: (param: string) => void;
  private _previousQ!: string | (string | null)[];
  private _previousIndex!: string | (string | null)[];

  created() {
    this._mapStore = getModule(MapStore, this.$store);
    this._debounceInput = debounce(this._search, 500);
  }

  @Watch('$route.query', { immediate: true, deep: true })
  onUrlChange(query: Route['query']) {
    const { q, index } = query;

    if (typeof q === 'string' && typeof index === 'string') {
      // exit if the query params are the same
      if (this._previousQ === q && this._previousIndex === index) return;

      const indexNumber = parseInt(index, 10);
      if (isNaN(indexNumber)) return;

      this.autocompleteIndex = indexNumber;

      const search = this._search(q);

      if (search) search.then(() => this._selectResult());
    } else {
      this.search = '';
      this.autocomplete = [];
      this.noResults();
    }

    this._previousQ = q;
    this._previousIndex = index;
  }

  getLocation() {
    const location: Record<string, string> = {
      laMeuse: 'Verdun, Meuse, France',
      valleeSud: 'Fontenay-aux-Roses, Hauts-de-Seine, France'
    };
    if (this.$route.query.index === '0' && this.$route.query.q === location[this.process]) {
      this._mapStore.setNewLastMarker([]);
      this._search(location[this.process]).then(() => {
        this.newLocation(this.autocomplete[0]);
        this.autocomplete = [];
      });
    } else {
      this._updateQueryParams(location[this.process], '0');
      this._selectResult();
    }
  }

  onBlur() {
    this.autocompleteIndex = null;
    this.autocomplete = [];
  }

  onChange() {
    this._debounceInput(this.search);
  }

  clearSearch() {
    this.search = '';
    this.autocomplete = [];
    this.noResults();
    this._updateQueryParams(undefined, undefined);
    this._mapStore.setNewLastMarker([]);
  }

  navigation(step: number) {
    this.autocompleteIndex = this.autocompleteIndex === null ? 0 : this.autocompleteIndex + step;

    // if the resultIndex is less than zero it goes to the last item in the list
    if (this.autocompleteIndex < 0) this.autocompleteIndex = this.autocomplete.length - 1;

    // if the result index is longer than the length of the list, it goes to the first item in the list
    if (this.autocompleteIndex > this.autocomplete.length - 1) this.autocompleteIndex = 0;
  }

  submit() {
    if (window.innerWidth < 750) {
      this.setBottomSheet('open');
    }
    if (!this.search.length) {
      this.noResults();
    } else if (this.autocompleteIndex === null) {
      this._search(this.search);
    } else {
      this._updateQueryParams(this.search, this.autocompleteIndex.toString());
      this._selectResult();
    }
  }

  private _search(search: string) {
    if (!search.length) return;

    return MapboxService.GetPlaces(search)
      .then(response => {
        this.autocomplete = response;
      })
      .catch(error => console.error(error));
  }

  private _selectResult() {
    if (this.autocompleteIndex === null) return;

    const selectedItem = this.autocomplete[this.autocompleteIndex];
    // check exist item in array when you select from query params
    if (!selectedItem) return;

    this.newLocation(selectedItem);

    this.search = selectedItem.place_name;
    this.autocompleteIndex = null;
    this.autocomplete = [];

    this.input.blur();
  }

  private _updateQueryParams(q: string | undefined, index: string | undefined): void {
    this.$router.push({ query: { q, index }, replace: true });
  }

  destroyed(): void {
    this.clearSearch();
  }
}
